import { useState, type FormEvent } from 'react';
import { beginEmailChange, completeEmailChange } from './email-change-api';
import '../styles/modules-my-account-email-change.css';

type EmailChangeStep = 'credentials' | 'codes';

const expiryFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function EmailChangePanel(): React.JSX.Element {
  const [step, setStep] = useState<EmailChangeStep>('credentials');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [currentAddressCode, setCurrentAddressCode] = useState('');
  const [newAddressCode, setNewAddressCode] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setPending(true);
    setError(null);
    try {
      const result = await beginEmailChange({ newEmail, currentPassword, totpCode });
      setCurrentPassword('');
      setTotpCode('');
      setExpiresAt(result.expiresAt);
      setStep('codes');
    } catch {
      setError(
        'Le changement d’adresse e-mail ne peut pas être démarré. Vérifiez les informations saisies.',
      );
    } finally {
      setPending(false);
    }
  }

  async function complete(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setPending(true);
    setError(null);
    try {
      await completeEmailChange({ currentAddressCode, newAddressCode });
      window.location.replace('/');
    } catch {
      setError(
        'Le changement d’adresse e-mail ne peut pas être confirmé. Vérifiez les deux codes reçus.',
      );
      setPending(false);
    }
  }

  if (step === 'codes') {
    return (
      <form className="my-account-email-form" onSubmit={(event) => void complete(event)}>
        <p className="my-account-email-help">
          Deux codes ont été envoyés : un à votre adresse actuelle et un à la nouvelle adresse.
          {expiresAt
            ? ` Ils sont valables jusqu’au ${expiryFormatter.format(new Date(expiresAt))}.`
            : ''}
        </p>
        <p className="my-account-email-help">
          Votre authentification à deux facteurs reste inchangée. Après validation, reconnectez-vous
          avec la nouvelle adresse e-mail et le même code TOTP généré par le compte Tosho déjà
          présent dans votre application d’authentification.
        </p>
        <label>
          <span>Code reçu sur l’adresse actuelle</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={currentAddressCode}
            onChange={(event) => setCurrentAddressCode(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Code reçu sur la nouvelle adresse</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={newAddressCode}
            onChange={(event) => setNewAddressCode(event.target.value)}
            required
          />
        </label>
        {error ? (
          <p className="my-account-email-error tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="my-account-email-actions">
          <button
            type="button"
            className="my-account-email-secondary"
            onClick={() => {
              setStep('credentials');
              setCurrentAddressCode('');
              setNewAddressCode('');
              setExpiresAt(null);
              setError(null);
            }}
            disabled={pending}
          >
            Recommencer
          </button>
          <button type="submit" className="my-account-email-primary" disabled={pending}>
            {pending ? 'Confirmation…' : 'Confirmer la nouvelle adresse'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="my-account-email-form" onSubmit={(event) => void begin(event)}>
      <p className="my-account-email-help">
        Pour protéger votre compte, Tosho vérifie votre mot de passe et votre code TOTP à 6 chiffres
        avant d’envoyer les codes de confirmation.
      </p>
      <p className="my-account-email-help">
        Le changement d’adresse e-mail ne modifie pas votre authentification à deux facteurs : le
        même compte Tosho de votre application d’authentification continuera à générer les codes.
      </p>
      <label>
        <span>Nouvelle adresse e-mail</span>
        <input
          type="email"
          autoComplete="email"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          required
        />
      </label>
      <label>
        <span>Mot de passe actuel</span>
        <input
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
        />
      </label>
      <label>
        <span>Code TOTP à 6 chiffres</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          pattern="[0-9]{6}"
          value={totpCode}
          onChange={(event) => setTotpCode(event.target.value)}
          required
        />
      </label>
      {error ? (
        <p className="my-account-email-error tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="my-account-email-actions">
        <button type="submit" className="my-account-email-primary" disabled={pending}>
          {pending ? 'Vérification…' : 'Continuer'}
        </button>
      </div>
    </form>
  );
}
